/**
 * Migration tooling v0 — typed wrappers around /v1/migrations per
 * TZ-04 §D. v0 is a synchronous single-HTML import: the POST returns a
 * job that is already completed (with template_id) or the request fails
 * with 422 SCHEMA_INCOMPATIBLE.
 */
import { apiClient } from "./apiClient";

export type SourcePlatform =
  | "sendpulse"
  | "stripo"
  | "beefree"
  | "mailchimp"
  | "generic";

export interface MigrationWarning {
  severity: "info" | "warning" | "error";
  code: string;
  message: string;
  block_id?: string;
  data?: Record<string, unknown>;
}

export interface MigrationJob {
  id: string;
  status: "queued" | "in_progress" | "completed" | "failed" | "cancelled";
  source_platform: string;
  template_id?: string | null;
  total_files: number;
  processed_files?: number;
  succeeded_files?: number;
  failed_files?: number;
  warnings_count?: number;
  warnings?: MigrationWarning[];
  /** Whole-job failure detail (code + detail); null/absent on success. */
  error?: { code?: string; detail?: string } | null;
  created_at?: string;
  started_at?: string | null;
  completed_at?: string | null;
}

export interface MigrationPage {
  data: MigrationJob[];
  pagination: { limit: number; next_cursor: string | null; has_more: boolean };
}

/** ≤20 MB zip — mirrors MigrationsController::MAX_ZIP_BYTES. */
export const MAX_ZIP_BYTES = 20_971_520;

export async function importHtml(input: {
  html: string;
  sourcePlatform: SourcePlatform;
  defaultLanguage?: string;
  name?: string;
  /** Copy external images into the asset library (Migration 2.2). Default true. */
  rehostImages?: boolean;
}): Promise<MigrationJob> {
  const response = await apiClient.post<{ data: MigrationJob }>(
    "/v1/migrations/import-html",
    {
      html: input.html,
      options: {
        source_platform: input.sourcePlatform,
        default_language: input.defaultLanguage ?? "en",
        rehost_images: input.rehostImages ?? true,
        ...(input.name !== undefined && input.name.trim() !== ""
          ? { name: input.name.trim() }
          : {}),
      },
    },
  );
  return response.data;
}

/**
 * Async bulk import — upload a zip of .html emails and get back a QUEUED
 * job; the worker converts each entry on one shared job (Phase 2.3). Poll
 * {@link getMigration} until status leaves queued/in_progress.
 */
export async function importBulk(input: {
  file: File;
  sourcePlatform: SourcePlatform;
  defaultLanguage?: string;
  rehostImages?: boolean;
}): Promise<MigrationJob> {
  const form = new FormData();
  form.append("file", input.file, input.file.name);
  form.append(
    "options",
    JSON.stringify({
      source_platform: input.sourcePlatform,
      default_language: input.defaultLanguage ?? "en",
      rehost_images: input.rehostImages ?? true,
    }),
  );
  const response = await apiClient.request<{ data: MigrationJob }>(
    "/v1/migrations/import-bulk",
    { method: "POST", rawBody: form },
  );
  return response.data;
}

/** Fetch one migration job — used to poll a bulk import to completion. */
export async function getMigration(id: string): Promise<MigrationJob> {
  const response = await apiClient.get<{ data: MigrationJob }>(
    `/v1/migrations/${id}`,
  );
  return response.data;
}

/** List this tenant's import jobs, newest first (cursor-paginated). */
export async function listMigrations(
  params: { limit?: number; cursor?: string; status?: MigrationJob["status"] } = {},
): Promise<MigrationPage> {
  const q = new URLSearchParams();
  if (params.limit !== undefined) q.set("limit", String(params.limit));
  if (params.cursor !== undefined && params.cursor !== "") q.set("cursor", params.cursor);
  if (params.status !== undefined) q.set("status", params.status);
  const qs = q.toString();
  return apiClient.get<MigrationPage>(`/v1/migrations${qs !== "" ? `?${qs}` : ""}`);
}
